import moment = require("moment");
import React = require("react");
import DatePicker from "react-datepicker";
import {I18n} from "../components/Utils/i18n";
import {AppTranslations} from "../components/Utils/translations/AppTranslations";
import "./filters.scss";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../index";

declare var AppTranslations: AppTranslations;
declare var LOCALE: string;

interface StateOption {
    value: string;
    text: string;
    isEmpty?: boolean;
}

interface DateOption {
    value: string;
    text: string;
    showDatepicker: boolean;
    getOptionStartDate: () => Date;
    getOptionEndDate: () => Date;
}

interface FilterProps {
    stateTitleSelect: string;
    periodTitleSelect: string;
    customPeriodFromTitle: string;
    customPeriodToTitle: string;
    defaultStateOption: string;
    defaultDateOption: string;
    stateOptions: StateOption[];
    dateOptions: DateOption[];
    onChange: (statusCode: string, startDate: Date, endDate: Date) => void;
    alreadyInitialized?: boolean;
}

interface FilterState {
    currentStateOptionValue: string;
    currentDateOptionValue: string;
    currentStartDate: Date;
    currentEndDate: Date;
}

export class StateWithPeriodFilter extends React.Component<FilterProps, FilterState> {

    public static equipmentTargetFilter(defaultStateOption: string,
                                        defaultDateOption: string,
                                        alreadyInitialized: boolean,
                                        onChange: (statusCode: string, startDate: Date, endDate: Date) => void) {
        const equipmentTargetTranslations = AppTranslations.filters.statuses.equipmentTarget;
        let stateOption = "-1";
        let dateOption = "1";
        if (defaultStateOption != null) {
            stateOption = defaultStateOption;
        }
        if (defaultDateOption != null) {
            dateOption = defaultDateOption;
        }
        return (
            <StateWithPeriodFilter
                stateTitleSelect={equipmentTargetTranslations.title}
                periodTitleSelect={equipmentTargetTranslations.when}
                customPeriodFromTitle={equipmentTargetTranslations.from}
                customPeriodToTitle={equipmentTargetTranslations.to}
                defaultStateOption={stateOption}
                defaultDateOption={dateOption}
                alreadyInitialized={alreadyInitialized}
                stateOptions={[
                    {value: "-1", text: "", isEmpty: true},
                    {value: "POSITIVE", text: equipmentTargetTranslations.aboveTarget, isEmpty: false},
                    {value: "NEGATIVE", text: equipmentTargetTranslations.belowTarget, isEmpty: false},
                ]}
                dateOptions={StateWithPeriodFilter.getGenericDateOptions()}
                onChange={onChange}
            />
        );
    }

    private static getGenericDateOptions(): DateOption[] {
        const equipmentTargetTranslations = AppTranslations.filters.statuses.equipmentTarget;
        return [
            {
                value: "1",
                text: equipmentTargetTranslations.today,
                showDatepicker: false,
                getOptionStartDate: (): Date => {
                    return moment().startOf("day").toDate();
                },
                getOptionEndDate: () => {
                    return moment().startOf("day").add(1, "day").toDate();
                },
            },
            {
                value: "2",
                text: equipmentTargetTranslations.lastWeek,
                showDatepicker: false,
                getOptionStartDate: () => {
                    return moment().startOf("day").add(-7, "day").toDate();
                },
                getOptionEndDate: () => {
                    return moment().startOf("day").add(1, "day").toDate();
                },
            },
            {
                value: "3",
                text: equipmentTargetTranslations.lastMonth,
                showDatepicker: false,
                getOptionStartDate: () => {
                    return moment().startOf("day").add(-30, "day").toDate();
                },
                getOptionEndDate: () => {
                    return moment().startOf("day").add(1, "day").toDate();
                },
            },
            {
                value: "4",
                text: equipmentTargetTranslations.customDate,
                showDatepicker: true,
                getOptionStartDate: () => {
                    return moment().startOf("day").toDate();
                },
                getOptionEndDate: () => {
                    return moment().startOf("day").add(1, "day").toDate();
                },
            },
        ];
    }

    constructor(props: FilterProps) {
        super(props);
        this.onStateChange = this.onStateChange.bind(this);
        this.onDateOptionChange = this.onDateOptionChange.bind(this);
        this.onStartDateChange = this.onStartDateChange.bind(this);
        this.onEndDateChange = this.onEndDateChange.bind(this);
        this.state = {
            currentStateOptionValue: this.props.defaultStateOption,
            currentDateOptionValue: this.props.defaultDateOption,
            currentStartDate: this.getOptionStartDate(this.props.defaultDateOption),
            currentEndDate: this.getOptionEndDate(this.props.defaultDateOption),
        };
    }

    public componentDidMount(): void {
        if (this.props.alreadyInitialized) {
            this.props.onChange(
                this.state.currentStateOptionValue,
                this.state.currentStartDate,
                this.state.currentEndDate,
            );
        }
    }

    public reset() {
        this.setState({
            currentStateOptionValue: "-1",
            currentDateOptionValue: "1",
            currentStartDate: this.getOptionStartDate(this.props.defaultDateOption),
        });
        this.props.onChange(null, null, null);
    }

    public render() {
        if (this.props.stateOptions == null || this.props.stateOptions.length === 0) {
            return null;
        }
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div className={"state-filter-with-datepicker"}>
                    {this.renderStateSelect()}
                    {this.renderDateOptionSelect()}
                    {this.renderStartDatepicker()}
                    {this.renderEndDatepicker()}
                </div>
            </ThemeProvider>
        );
    }

    private onStateChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const option = this.findStateOption(e.target.value);
        this.setState({currentStateOptionValue: option.value});
        this.props.onChange(
            option.isEmpty ? null : e.target.value,
            this.state.currentStartDate,
            this.state.currentEndDate,
        );
    }

    private onDateOptionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const option = this.findDateOption(e.target.value);
        const date = option.getOptionStartDate();
        this.setState({currentStartDate: date, currentDateOptionValue: option.value});
        this.props.onChange(
            this.state.currentStateOptionValue,
            date,
            option.getOptionEndDate(),
        );
    }

    private onStartDateChange(date: Date) {
        this.setState({currentStartDate: date});
        this.props.onChange(
            this.state.currentStateOptionValue,
            date,
            this.state.currentEndDate != null ?
                this.state.currentEndDate :
                moment().startOf("day").add(1, "day").toDate(),
        );
    }

    private onEndDateChange(date: Date) {
        this.setState({currentEndDate: date});
        this.props.onChange(
            this.state.currentStateOptionValue,
            this.state.currentStartDate,
            date,
        );
    }

    private renderStateSelect() {
        const options: JSX.Element[] = [];
        for (let i = 0; i < this.props.stateOptions.length; i++) {
            options.push(
                (
                    <option value={this.props.stateOptions[i].value} key={i}>
                        {this.props.stateOptions[i].text}
                    </option>
                ),
            );
        }
        let selected = this.state.currentStateOptionValue;
        if (selected == null) {
            selected = this.props.defaultStateOption;
        }
        let classNames = "filterTitleMaterial";
        const option = this.findStateOption(this.state.currentStateOptionValue);
        if (!option.isEmpty) {
            classNames += " filtersGroupTitleEnlight";
        }
        return (
            <React.Fragment>
                <p className={classNames}>{this.props.stateTitleSelect}</p>
                <select className="groupFilterSelect" onChange={this.onStateChange} value={selected}>
                    {options}
                </select>
            </React.Fragment>
        );
    }

    private renderDateOptionSelect() {
        const stateOption = this.findStateOption(this.state.currentStateOptionValue);
        if (stateOption == null || stateOption.isEmpty) {
            return null;
        }
        const options: JSX.Element[] = [];
        for (let i = 0; i < this.props.dateOptions.length; i++) {
            options.push(
                (
                    <option value={this.props.dateOptions[i].value} key={i}>
                        {this.props.dateOptions[i].text}
                    </option>
                ),
            );
        }
        let selected = this.state.currentDateOptionValue;
        if (selected == null) {
            selected = this.props.defaultDateOption;
        }
        return (
            <React.Fragment>
                <p className={"filterTitleMaterial"}>{this.props.periodTitleSelect}</p>
                <select className="groupFilterSelect" onChange={this.onDateOptionChange} value={selected}>
                    {options}
                </select>
            </React.Fragment>
        );
    }

    private renderStartDatepicker() {
        const dateOption = this.findDateOption(this.state.currentDateOptionValue);
        const stateOption = this.findStateOption(this.state.currentStateOptionValue);
        if (stateOption.isEmpty || dateOption == null || !dateOption.showDatepicker) {
            return null;
        }
        return (
            <React.Fragment>
                <p className={"filterTitleMaterial"}>{this.props.customPeriodFromTitle}</p>
                <div className={"filterContent filterContentDatePicker"}>
                    <DatePicker
                        selected={this.state.currentStartDate}
                        maxDate={moment().startOf("day").add(1, "day").toDate()}
                        onChange={this.onStartDateChange}
                        dateFormat={I18n.getLocaleDateFormat(LOCALE)}
                        // locale={LOCALE}
                        withPortal={false}
                    />
                </div>
            </React.Fragment>
        );
    }

    private renderEndDatepicker() {
        const dateOption = this.findDateOption(this.state.currentDateOptionValue);
        const stateOption = this.findStateOption(this.state.currentStateOptionValue);
        if (stateOption.isEmpty || dateOption == null || !dateOption.showDatepicker) {
            return null;
        }
        return (
            <React.Fragment>
                <p className={"filterTitleMaterial"}>{this.props.customPeriodToTitle}</p>
                <div className={"filterContent filterContentDatePicker"}>
                    <DatePicker
                        selected={this.state.currentEndDate}
                        maxDate={moment().startOf("day").add(1, "day").toDate()}
                        onChange={this.onEndDateChange}
                        dateFormat={I18n.getLocaleDateFormat(LOCALE)}
                        // locale={LOCALE}
                        withPortal={false}
                    />
                </div>
            </React.Fragment>
        );
    }

    private getOptionStartDate(value: string): Date {
        const option = this.findDateOption(value);
        if (option != null && option.getOptionStartDate) {
            return option.getOptionStartDate();
        }
        return null;
    }

    private getOptionEndDate(value: string): Date {
        const option = this.findDateOption(value);
        if (option != null && option.getOptionEndDate) {
            return option.getOptionEndDate();
        }
        return null;
    }

    private findDateOption(value: string): DateOption {
        if (value === null || this.props.dateOptions === null || this.props.dateOptions.length === 0) {
            return null;
        }
        for (const dateOption of this.props.dateOptions) {
            if (dateOption.value === value) {
                return dateOption;
            }
        }
        return null;
    }

    private findStateOption(value: string): StateOption {
        if (value === null || this.props.stateOptions === null || this.props.stateOptions.length === 0) {
            return null;
        }
        for (const option of this.props.stateOptions) {
            if (option.value === value) {
                return option;
            }
        }
        return null;
    }
}
